import {initialAuthenticationState} from '@app/stores/authentication/authentication.state';
import {
  LoginError,
  LoginFlowRequired,
  LoginSuccess,
  LoginUser,
  LogoutUser,
  ForgetPassword,
  ForgetPasswordSuccess,
  ForgetPasswordError,
  ResetPassword,
  ResetPasswordSuccess,
  ResetPasswordError,
  ResetPasswordToken,
  ResetPasswordTokenSuccess,
  ResetAuthState,
  LoginUserWithIdSuccess, LoginUserWithIdError, LogoutSuccess, LogoutError,
  EndImpersonationError
} from '@app/stores/authentication/authentication.actions';
import {Action, createReducer, on} from '@ngrx/store';
import {User} from '@app/modules/shared/models';

const restoreUserModel = (user: User): User => Object.assign(new User(), user);

/**
 * Every transient field of the authentication state, at its empty value.
 *
 * Each handler below used to list its siblings by hand, so a new field had to be added in
 * about twenty places or it survived into the next attempt - a spent challenge token, a
 * stale flow, an error from the previous sign-in shown under a fresh one. Spreading this
 * instead means a field added here once is cleared everywhere a flow starts or ends.
 *
 * `user` and `impersonating` are deliberately not in it. They are what a handler decides
 * for itself, and several handlers keep the user they already hold.
 */
const CLEARED = {
  forgetPassword: null,
  resetPassword: null,
  passwordContext: null,
  success: '',
  error: '',
  authFlow: null
};

const generateReducer = createReducer(initialAuthenticationState,
  on(LoginUser, (state) => ({
    ...state,
    ...CLEARED,
    user: null
  })),
  on(LoginSuccess, (state, {user, impersonating}) => ({
    ...state,
    ...CLEARED,
    user: restoreUserModel(user),
    success: 'Login successful',
    // Omitted means unchanged: a profile save re-dispatches the same session, and used to
    // make the panel forget it was impersonating someone.
    impersonating: impersonating ?? state.impersonating
  })),
  on(LoginError, (state, {error}) => ({
    ...state,
    ...CLEARED,
    user: null,
    error
  })),
  on(LoginFlowRequired, (state, {flow}) => ({
    ...state,
    ...CLEARED,
    user: null,
    authFlow: flow
  })),
  on(LogoutUser, (state) => ({
    ...state,
    ...CLEARED,
    user: null
  })),
  on(LogoutSuccess, (state) => ({
    ...state,
    ...CLEARED,
    user: null,
    impersonating: false
  })),
  on(LogoutError, (state, {error}) => ({
    ...state,
    ...CLEARED,
    user: null,
    error
  })),

  on(ForgetPassword, (state) => ({
    ...state,
    ...CLEARED,
    user: null
  })),
  on(ForgetPasswordSuccess, (state, {forgetPassword}) => ({
    ...state,
    ...CLEARED,
    user: null,
    forgetPassword,
    success: 'Temporary password sent successful'
  })),
  on(ForgetPasswordError, (state, {error}) => ({
    ...state,
    ...CLEARED,
    user: null,
    error
  })),

  on(ResetPassword, (state) => ({
    ...state,
    ...CLEARED,
    user: null
  })),
  // Mid-flow, not a boundary: validating the token must not throw away the reset the user
  // is in the middle of. These three deliberately do not spread CLEARED.
  on(ResetPasswordToken, (state) => ({
    ...state,
    passwordContext: null,
    error: ''
  })),
  on(ResetPasswordSuccess, (state, {resetPassword}) => ({
    ...state,
    ...CLEARED,
    user: null,
    resetPassword,
    success: 'Password reset successful'
  })),
  on(ResetPasswordTokenSuccess, (state, {passwordContext}) => ({
    ...state,
    passwordContext,
    error: ''
  })),
  on(ResetPasswordError, (state, {error}) => ({
    ...state,
    ...CLEARED,
    user: null,
    error
  })),
  on(LoginUserWithIdSuccess, (state, {user}) => ({
    ...state,
    ...CLEARED,
    user: restoreUserModel(user),
    success: 'LOGIN_WITH_ID_SUCCESSFUL',
    impersonating: true
  })),
  // A refused "Login as" leaves the actor signed in as they were.
  on(LoginUserWithIdError, (state, {error}) => ({
    ...state,
    ...CLEARED,
    error
  })),
  on(EndImpersonationError, (state, {error}) => ({
    ...state,
    success: '',
    error
  })),

  on(ResetAuthState, (state, {params}) => ({
    ...state,
    ...params
  })),
);

export function authenticationReducer(state = initialAuthenticationState, action: Action) {
  return generateReducer(state, action);
}
